/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Alert,
  Share,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const SavedInternships = ({ route }) => {
  const navigation = useNavigation();
  const [savedInternships, setSavedInternships] = useState([]);
  const [appliedInternships, setAppliedInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInternship, setSelectedInternship] = useState(null);
  
  const allInternships = [
    {
      id: 'INT001',
      title: 'Software Engineering Intern',
      company: 'Google',
      logo: 'https://cdn-icons-png.flaticon.com/512/300/300221.png',
      location: 'London, UK',
      field: 'software',
      type: 'paid',
      duration: 'summer',
      salary: '£4,500/month',
      applicationDeadline: '2024-03-15',
      startDate: '2024-06-01',
      endDate: '2024-08-31',
      description: 'Join Google\'s engineering team for a summer internship. Work on cutting-edge projects in cloud computing and machine learning.',
      requirements: [
        'Currently pursuing a BS/MS in Computer Science',
        'Strong programming skills in Python/Java',
        'Experience with data structures and algorithms',
        'Knowledge of web technologies'
      ],
      responsibilities: [
        'Design and implement software solutions',
        'Collaborate with cross-functional teams',
        'Write clean, maintainable code',
        'Participate in code reviews'
      ],
      benefits: [
        'Competitive salary',
        'Housing stipend',
        'Mentorship program',
        'Networking events',
        'Potential return offer'
      ],
      applyLink: 'https://careers.google.com',
      contactEmail: 'internships@google.com',
      spots: 25,
      applications: 1250,
      featured: true,
      urgent: true,
    },
    {
      id: 'INT002',
      title: 'Mechanical Design Intern',
      company: 'Rolls-Royce',
      logo: 'https://cdn-icons-png.flaticon.com/512/841/841364.png',
      location: 'Derby, UK',
      field: 'mechanical',
      type: 'paid',
      duration: 'year',
      salary: '£2,800/month',
      applicationDeadline: '2024-04-30',
      startDate: '2024-09-01',
      endDate: '2025-08-31',
      description: 'Year-long placement in aerospace engineering. Work on propulsion systems and mechanical design.',
      requirements: [
        'Mechanical Engineering student',
        'CAD proficiency (SolidWorks/CATIA)',
        'Understanding of thermodynamics',
        'Strong analytical skills'
      ],
      responsibilities: [
        'Assist in design and analysis',
        'Create technical drawings',
        'Conduct simulations',
        'Prepare documentation'
      ],
      benefits: [
        'Industry experience',
        'Professional training',
        'Travel opportunities',
        'Company pension'
      ],
      applyLink: 'https://careers.rolls-royce.com',
      contactEmail: 'early.careers@rolls-royce.com',
      spots: 15,
      applications: 680,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT003',
      title: 'Civil Engineering Intern',
      company: 'Arup',
      logo: 'https://cdn-icons-png.flaticon.com/512/3220/3220315.png',
      location: 'Manchester, UK',
      field: 'civil',
      type: 'paid',
      duration: 'semester',
      salary: '£2,500/month',
      applicationDeadline: '2024-03-31',
      startDate: '2024-09-01',
      endDate: '2025-02-28',
      description: 'Semester internship focusing on sustainable infrastructure projects.',
      requirements: [
        'Civil Engineering student',
        'Knowledge of structural analysis',
        'AutoCAD proficiency',
        'Interest in sustainability'
      ],
      responsibilities: [
        'Site inspections',
        'Structural calculations',
        'Project documentation',
        'Client meetings'
      ],
      benefits: [
        'Hands-on site experience',
        'Mentorship',
        'Networking',
        'Training courses'
      ],
      applyLink: 'https://www.arup.com/careers',
      contactEmail: 'graduates@arup.com',
      spots: 20,
      applications: 450,
      featured: false,
      urgent: true,
    },
    {
      id: 'INT004',
      title: 'Electrical Engineering Intern',
      company: 'Siemens',
      logo: 'https://cdn-icons-png.flaticon.com/512/5968/5968350.png',
      location: 'Berlin, Germany',
      field: 'electrical',
      type: 'paid',
      duration: 'summer',
      salary: '€3,200/month',
      applicationDeadline: '2024-04-15',
      startDate: '2024-07-01',
      endDate: '2024-09-30',
      description: 'Summer internship in renewable energy systems and smart grid technology.',
      requirements: [
        'Electrical Engineering student',
        'Circuit design knowledge',
        'Power systems understanding',
        'German language optional'
      ],
      responsibilities: [
        'Circuit design and testing',
        'Data analysis',
        'Report writing',
        'Team collaboration'
      ],
      benefits: [
        'International experience',
        'Language courses',
        'Housing assistance',
        'Travel reimbursement'
      ],
      applyLink: 'https://new.siemens.com/global/en/company/jobs.html',
      contactEmail: 'internships@siemens.com',
      spots: 30,
      applications: 920,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT005',
      title: 'Data Science Intern',
      company: 'Amazon',
      logo: 'https://cdn-icons-png.flaticon.com/512/731/731985.png',
      location: 'Remote',
      field: 'data',
      type: 'paid',
      duration: 'flexible',
      salary: '$6,500/month',
      applicationDeadline: '2024-05-01',
      startDate: '2024-06-01',
      endDate: '2024-12-31',
      description: 'Remote internship in data analytics and machine learning for e-commerce.',
      requirements: [
        'Strong Python/R skills',
        'SQL proficiency',
        'Machine learning knowledge',
        'Statistical analysis'
      ],
      responsibilities: [
        'Data analysis and visualization',
        'Model development',
        'A/B testing',
        'Insight generation'
      ],
      benefits: [
        'Flexible hours',
        'Remote work',
        'Stock options',
        'Amazon perks'
      ],
      applyLink: 'https://www.amazon.jobs',
      contactEmail: 'student-programs@amazon.com',
      spots: 40,
      applications: 2100,
      featured: true,
      urgent: true,
    },
    {
      id: 'INT006',
      title: 'Chemical Engineering Intern',
      company: 'BP',
      logo: 'https://cdn-icons-png.flaticon.com/512/3208/3208726.png',
      location: 'Houston, USA',
      field: 'chemical',
      type: 'paid',
      duration: 'summer',
      salary: '$5,200/month',
      applicationDeadline: '2024-03-20',
      startDate: '2024-05-27',
      endDate: '2024-08-16',
      description: 'Summer internship in petroleum refining and process engineering.',
      requirements: [
        'Chemical Engineering student',
        'Process design knowledge',
        'Laboratory experience',
        'Safety awareness'
      ],
      responsibilities: [
        'Process optimization',
        'Safety analysis',
        'Data collection',
        'Technical reporting'
      ],
      benefits: [
        'Competitive pay',
        'Housing provided',
        'Field trips',
        'Mentorship'
      ],
      applyLink: 'https://www.bp.com/en/global/corporate/careers.html',
      contactEmail: 'early.careers@bp.com',
      spots: 18,
      applications: 760,
      featured: false,
      urgent: false,
    },
    {
      id: 'INT007',
      title: 'Aerospace Engineering Intern',
      company: 'Airbus',
      logo: 'https://cdn-icons-png.flaticon.com/512/809/809957.png',
      location: 'Toulouse, France',
      field: 'aerospace',
      type: 'paid',
      duration: 'semester',
      salary: '€2,800/month',
      applicationDeadline: '2024-04-10',
      startDate: '2024-09-01',
      endDate: '2025-02-28',
      description: 'Semester internship in aircraft design and aerodynamics.',
      requirements: [
        'Aerospace Engineering student',
        'Aerodynamics knowledge',
        'CAD experience',
        'French language preferred'
      ],
      responsibilities: [
        'Aerodynamic analysis',
        'Design support',
        'Testing coordination',
        'Documentation'
      ],
      benefits: [
        'French language course',
        'European travel',
        'Industry exposure',
        'Networking events'
      ],
      applyLink: 'https://www.airbus.com/en/careers',
      contactEmail: 'internships@airbus.com',
      spots: 22,
      applications: 890,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT008',
      title: 'Biomedical Engineering Intern',
      company: 'Medtronic',
      logo: 'https://cdn-icons-png.flaticon.com/512/2785/2785352.png',
      location: 'Minneapolis, USA',
      field: 'biomedical',
      type: 'paid',
      duration: 'summer',
      salary: '$4,800/month',
      applicationDeadline: '2024-03-25',
      startDate: '2024-06-03',
      endDate: '2024-08-23',
      description: 'Summer internship in medical device development and testing.',
      requirements: [
        'Biomedical Engineering student',
        'Biology/physiology knowledge',
        'Lab experience',
        'Regulatory awareness'
      ],
      responsibilities: [
        'Device testing',
        'Protocol development',
        'Data analysis',
        'Quality assurance'
      ],
      benefits: [
        'Healthcare experience',
        'Professional development',
        'Networking',
        'Potential full-time offer'
      ],
      applyLink: 'https://www.medtronic.com/us-en/careers.html',
      contactEmail: 'campus.recruiting@medtronic.com',
      spots: 16,
      applications: 540,
      featured: false,
      urgent: true,
    },
  ];

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [])
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [saved, applied, darkModePref] = await Promise.all([
        AsyncStorage.getItem('savedInternships'),
        AsyncStorage.getItem('appliedInternships'),
        AsyncStorage.getItem('darkMode'),
      ]);
      
      if (saved) {
        setSavedInternships(JSON.parse(saved));
      }
      
      if (applied) {
        setAppliedInternships(JSON.parse(applied));
      }
      
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading saved internships:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const toggleSaveInternship = async (internshipId) => {
    try {
      let newSaved;
      if (savedInternships.includes(internshipId)) {
        newSaved = savedInternships.filter(id => id !== internshipId);
      } else {
        newSaved = [...savedInternships, internshipId];
      }
      
      setSavedInternships(newSaved);
      await AsyncStorage.setItem('savedInternships', JSON.stringify(newSaved));
      
      if (!newSaved.includes(internshipId)) {
        Alert.alert('Removed', 'Internship removed from saved list');
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const applyToInternship = async (internshipId) => {
    try {
      const newApplied = [...appliedInternships, internshipId];
      setAppliedInternships(newApplied);
      await AsyncStorage.setItem('appliedInternships', JSON.stringify(newApplied));
      
      Alert.alert(
        'Application Submitted',
        'Your application has been submitted successfully!',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error applying to internship:', error);
    }
  };

  const navigateToApply = (internship) => {
    navigation.navigate('InternshipApply', { 
      internship,
      onApplySuccess: () => {
        applyToInternship(internship.id);
      }
    });
  };

  const openApplicationLink = (link) => {
    Linking.openURL(link).catch(err => {
      console.error('Failed to open link:', err);
      Alert.alert('Error', 'Could not open application link');
    });
  };

  const shareInternship = async (internship) => {
    try {
      await Share.share({
        title: internship.title,
        message: `Check out this internship: ${internship.title} at ${internship.company}\n\nLocation: ${internship.location}\nDeadline: ${internship.applicationDeadline}\n\nApply here: ${internship.applyLink}`,
        url: internship.applyLink,
      });
    } catch (error) {
      console.error('Error sharing internship:', error);
    }
  };

  const calculateDaysLeft = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getFieldColor = (field) => {
    const colors = darkMode ? {
      software: ['#4c51bf', '#667eea'],
      mechanical: ['#dd6b20', '#ed8936'],
      civil: ['#2b6cb0', '#4299e1'],
      electrical: ['#d69e2e', '#ecc94b'],
      chemical: ['#38a169', '#68d391'],
      aerospace: ['#9f7aea', '#b794f4'],
      biomedical: ['#ed64a6', '#f687b3'],
      data: ['#4c51bf', '#667eea'],
      default: ['#718096', '#a0aec0'],
    } : {
      software: ['#6366f1', '#818cf8'],
      mechanical: ['#f97316', '#fb923c'],
      civil: ['#3b82f6', '#60a5fa'],
      electrical: ['#eab308', '#facc15'],
      chemical: ['#10b981', '#34d399'],
      aerospace: ['#8b5cf6', '#a78bfa'],
      biomedical: ['#ec4899', '#f472b6'],
      data: ['#6366f1', '#818cf8'],
      default: ['#6b7280', '#9ca3af'],
    };
    
    return colors[field] || colors.default;
  };

  const getFieldIcon = (field) => {
    const icons = {
      software: 'code-braces',
      mechanical: 'cog',
      civil: 'bridge',
      electrical: 'flash',
      chemical: 'flask',
      aerospace: 'airplane',
      biomedical: 'heart-pulse',
      data: 'chart-bar',
      default: 'briefcase',
    };
    
    return icons[field] || icons.default;
  };

  const filteredInternships = allInternships.filter(internship => 
    savedInternships.includes(internship.id)
  );

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>📚 Saved Internships</Text>
        <Text style={styles.headerSubtitle}>
          {filteredInternships.length} saved opportunities
        </Text>
      </View>
      <View style={styles.placeholder} />
    </LinearGradient>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name="bookmark-outline" 
        size={64} 
        color={darkMode ? "#4a5568" : "#d1d5db"} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Saved Internships
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Save internships you're interested in by tapping the bookmark icon
      </Text>
      <TouchableOpacity 
        style={styles.browseButton}
        onPress={() => navigation.navigate('Internships')}
      >
        <Text style={styles.browseButtonText}>Browse Internships</Text>
      </TouchableOpacity>
    </View>
  );

  const renderInternshipItem = ({ item, index }) => {
    const isApplied = appliedInternships.includes(item.id);
    const daysLeft = calculateDaysLeft(item.applicationDeadline);
    
    return (
      <TouchableOpacity
        style={styles.internshipCard}
        onPress={() => {
          setSelectedInternship(item);
          setDetailModalVisible(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.internshipHeader}>
          <LinearGradient
            colors={getFieldColor(item.field)}
            style={styles.internshipField}
          >
            <MaterialCommunityIcons name={getFieldIcon(item.field)} size={16} color="#ffffff" />
            <Text style={styles.internshipFieldText}>{String(item.field ?? '').toUpperCase()}</Text>
          </LinearGradient>
          
          <TouchableOpacity 
            style={styles.saveButton}
            onPress={() => toggleSaveInternship(item.id)}
          >
            <MaterialCommunityIcons 
              name="bookmark" 
              size={18} 
              color="#667eea" 
            />
          </TouchableOpacity>
        </View>

        <View style={styles.internshipContent}>
          <Text style={styles.internshipTitle} numberOfLines={2}>
            {item.title}
          </Text>
          
          <View style={styles.companyContainer}>
            <View style={styles.companyLogo}>
              <MaterialCommunityIcons name="office-building" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            </View>
            <Text style={styles.companyText} numberOfLines={1}>
              {item.company}
            </Text>
          </View>

          <View style={styles.internshipMeta}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="map-marker" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.location}</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.duration}</Text>
            </View>
          </View>

          <View style={styles.internshipStats}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="currency-usd" size={12} color="#10b981" />
              <Text style={styles.statText}>{item.salary}</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="clock" size={12} color="#f59e0b" />
              <Text style={styles.statText}>{daysLeft}d left</Text>
            </View>
          </View>

          {item.featured && (
            <View style={styles.featuredBadge}>
              <MaterialCommunityIcons name="star" size={10} color="#ffffff" />
              <Text style={styles.featuredText}>Featured</Text>
            </View>
          )}

          {item.urgent && (
            <View style={styles.urgentBadge}>
              <MaterialCommunityIcons name="alert" size={10} color="#ffffff" />
              <Text style={styles.urgentText}>Urgent</Text>
            </View>
          )}
        </View>

        <View style={styles.internshipActions}>
          {isApplied ? (
            <TouchableOpacity 
              style={styles.appliedButton}
              disabled={true}
            >
              <MaterialCommunityIcons name="check-circle" size={14} color="#ffffff" />
              <Text style={styles.appliedText}>Applied</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={styles.applyButton}
              onPress={() => navigateToApply(item)}
            >
              <Text style={styles.applyText}>Apply Now</Text>
              <MaterialCommunityIcons name="arrow-right" size={14} color="#ffffff" />
            </TouchableOpacity>
          )}
          
          <TouchableOpacity 
            style={styles.removeButton}
            onPress={() => toggleSaveInternship(item.id)}
          >
            <Text style={styles.removeText}>Remove</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderDetailModal = () => (
    <Modal
      visible={detailModalVisible}
      animationType="slide"
      onRequestClose={() => setDetailModalVisible(false)}
    >
      <SafeAreaView style={[styles.detailModalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        {selectedInternship && (
          <>
            <LinearGradient
              colors={getFieldColor(selectedInternship.field)}
              style={styles.detailHeader}
            >
              <TouchableOpacity 
                style={styles.detailBackButton}
                onPress={() => setDetailModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={22} color="#ffffff" />
              </TouchableOpacity>
              <View style={styles.detailHeaderCenter}>
                <Text style={styles.detailTitle} numberOfLines={2}>
                  {selectedInternship.title}
                </Text>
                <Text style={styles.detailSubtitle}>
                  {selectedInternship.company} • {selectedInternship.location}
                </Text>
              </View>
              <TouchableOpacity 
                style={styles.detailShareButton}
                onPress={() => shareInternship(selectedInternship)}
              >
                <MaterialCommunityIcons name="share-variant" size={22} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>

            <ScrollView style={styles.detailContent}>
              <View style={styles.detailOverview}>
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="calendar-range" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Duration
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.duration}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="currency-usd" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Salary
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.salary}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="clock-outline" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Deadline
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.applicationDeadline}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="account-group" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Spots
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.spots} available
                  </Text>
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Description
                </Text>
                <Text style={[styles.sectionContent, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {selectedInternship.description}
                </Text>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Requirements
                </Text>
                {selectedInternship.requirements.map((req, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {req}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Responsibilities
                </Text>
                {selectedInternship.responsibilities.map((resp, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="arrow-right-circle" size={14} color="#667eea" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {resp}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Benefits
                </Text>
                {selectedInternship.benefits.map((benefit, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="gift" size={14} color="#f59e0b" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>
            </ScrollView>

            <View style={[styles.detailFooter, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
              <TouchableOpacity 
                style={styles.removeDetailButton}
                onPress={() => {
                  toggleSaveInternship(selectedInternship.id);
                  setDetailModalVisible(false);
                }}
              >
                <MaterialCommunityIcons 
                  name="bookmark-remove" 
                  size={20} 
                  color={darkMode ? "#a0aec0" : "#6b7280"} 
                />
                <Text style={[styles.removeDetailText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
                  Remove
                </Text>
              </TouchableOpacity>
              
              {appliedInternships.includes(selectedInternship.id) ? (
                <TouchableOpacity 
                  style={styles.appliedDetailButton}
                  disabled={true}
                >
                  <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
                  <Text style={styles.appliedDetailText}>Already Applied</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.applyDetailButton}
                  onPress={() => {
                    setDetailModalVisible(false);
                    navigateToApply(selectedInternship);
                  }}
                >
                  <Text style={styles.applyDetailText}>Apply Now</Text>
                  <MaterialCommunityIcons name="arrow-right" size={18} color="#ffffff" />
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading saved internships...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      
      {filteredInternships.length === 0 ? (
        renderEmptyState()
      ) : (
        <FlatList
          data={filteredInternships}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.scrollContent}
          renderItem={renderInternshipItem}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={['#667eea']}
              tintColor="#667eea"
            />
          }
          showsVerticalScrollIndicator={false}
          style={styles.scrollView}
          ListHeaderComponent={
            <View style={styles.statsContainer}>
              <Text style={[styles.statsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Your Saved Internships
              </Text>
              <Text style={[styles.statsSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {filteredInternships.length} internships saved • {appliedInternships.filter(id => 
                  filteredInternships.some(internship => internship.id === id)
                ).length} applied
              </Text>
            </View>
          }
        />
      )}

      {renderDetailModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  placeholder: {
    width: 34,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
    paddingTop: 8,
  },
  statsContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  statsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statsSubtitle: {
    fontSize: 13,
  },
  internshipCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginHorizontal: 16,
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  internshipHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingTop: 14,
  },
  internshipField: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  internshipFieldText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 6,
  },
  saveButton: {
    padding: 4,
  },
  internshipContent: {
    padding: 14,
  },
  internshipTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
    lineHeight: 20,
  },
  companyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  companyLogo: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  companyText: {
    fontSize: 12,
    color: '#6b7280',
    flex: 1,
  },
  internshipMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 6,
  },
  internshipStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 8,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginLeft: 6,
  },
  featuredBadge: {
    position: 'absolute',
    top: 14,
    right: 14,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  featuredText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 4,
  },
  urgentBadge: {
    position: 'absolute',
    top: 40,
    right: 14,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  urgentText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 4,
  },
  internshipActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  appliedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 2,
    justifyContent: 'center',
  },
  appliedText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 2,
    justifyContent: 'center',
  },
  applyText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginRight: 6,
  },
  removeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginLeft: 12,
    flex: 1,
    alignItems: 'center',
  },
  removeText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 16,
  },
  emptyStateText: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  browseButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  browseButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  detailModalContainer: {
    flex: 1,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  detailBackButton: {
    padding: 6,
  },
  detailHeaderCenter: {
    flex: 1,
    marginHorizontal: 10,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  detailSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  detailShareButton: {
    padding: 6,
  },
  detailContent: {
    flex: 1,
    padding: 16,
  },
  detailOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    gap: 12,
  },
  overviewItem: {
    width: '48%',
  },
  overviewLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  overviewValue: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  detailSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  listText: {
    fontSize: 13,
    lineHeight: 18,
    marginLeft: 8,
    flex: 1,
  },
  detailFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  removeDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginRight: 12,
  },
  removeDetailText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  appliedDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  appliedDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  applyDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  applyDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 6,
  },
});

export default SavedInternships;